/**
 * Keep Tab inside the editor, with CodeMirror's "tab focus mode" as the exit.
 *
 * Screenplay editing gives Tab a meaning, so unhandled Tab must not move focus.
 * WCAG 2.1.2 (No Keyboard Trap) still requires a documented exit, so this
 * mirrors CodeMirror 6 rather than inventing one:
 *
 * - Control-M (Shift-Option-M on Mac) toggles whether Tab moves focus.
 * - Toggling announces the new mode through a polite live region.
 * - Escape, when nothing else handles it, lets Tab move focus for the next two
 *   seconds, until any other non-modifier key is pressed.
 *
 * @see https://codemirror.net/examples/tab/
 * @see https://github.com/codemirror/view/blob/main/src/input.ts
 * @see https://github.com/codemirror/commands/blob/main/src/commands.ts
 */

import { keydownHandler } from 'prosemirror-keymap'
import { EditorState, Plugin, PluginKey } from 'prosemirror-state'
import { EditorView } from 'prosemirror-view'

import {
  detectedPlatform,
  keysFromProsemirror,
  Platform,
} from '#hooks/useShortcuts/helpers'

type TabFocusMode = 'off' | 'on' | { until: number }

const ESCAPE_RELEASE_MS = 2000

const focusLockKey = new PluginKey<TabFocusMode>('focusLock')

const MODIFIER_KEYS = new Set([
  'Alt',
  'AltGraph',
  'CapsLock',
  'Control',
  'Meta',
  'Shift',
])

const ANNOUNCEMENTS = {
  on: 'Tab moves focus',
  off: 'Tab stays in the editor',
}

const TOGGLES = {
  mac: { binding: 'Shift-Alt-m', spoken: 'Shift+Option+M' },
  win: { binding: 'Ctrl-m', spoken: 'Control+M' },
}

/** The keys that toggle tab focus mode, for display with buildShortcutTip. */
export function tabFocusToggleKeys(platform: Platform = detectedPlatform) {
  return keysFromProsemirror(TOGGLES[platform].binding)
}

/**
 * Keep unhandled Tab in the editor regardless of modifiers, unless tab focus
 * mode is active. Register after behavioral keymaps so they win Tab, Escape,
 * and the toggle.
 */
export function createFocusLockPlugin(platform: Platform = detectedPlatform) {
  const handleToggle = keydownHandler({
    [TOGGLES[platform].binding]: (state, dispatch) => {
      const next = modeOf(state) === 'on' ? 'off' : 'on'
      dispatch?.(state.tr.setMeta(focusLockKey, next))
      return true
    },
  })

  function modeOf(state: EditorState): TabFocusMode {
    return focusLockKey.getState(state) ?? 'off'
  }

  function setMode(view: EditorView, mode: TabFocusMode) {
    view.dispatch(view.state.tr.setMeta(focusLockKey, mode))
  }

  return new Plugin<TabFocusMode>({
    key: focusLockKey,
    state: {
      init: () => 'off',
      apply: (tr, mode) => tr.getMeta(focusLockKey) ?? mode,
    },
    view(view) {
      const announcer = createAnnouncer(view.dom.ownerDocument)
      return {
        update(view, prevState) {
          const isOn = modeOf(view.state) === 'on'
          if (isOn !== (modeOf(prevState) === 'on')) {
            announcer.textContent = ANNOUNCEMENTS[isOn ? 'on' : 'off']
          }
        },
        destroy() {
          announcer.remove()
        },
      }
    },
    props: {
      attributes: {
        'aria-description':
          `Press ${TOGGLES[platform].spoken} to toggle whether Tab moves ` +
          'focus, or Escape then Tab to leave the editor.',
      },
      handleDOMEvents: {
        // Runs before every handleKeyDown; true skips them without preventDefault.
        keydown(view, event) {
          const mode = modeOf(view.state)
          if (event.key === 'Tab' && isActive(mode, Date.now())) {
            return true
          }
          if (
            typeof mode === 'object' &&
            event.key !== 'Escape' &&
            !MODIFIER_KEYS.has(event.key)
          ) {
            setMode(view, 'off')
          }
          return false
        },
      },
      handleKeyDown(view, event) {
        if (handleToggle(view, event)) return true
        if (event.key === 'Escape' && modeOf(view.state) !== 'on') {
          setMode(view, { until: Date.now() + ESCAPE_RELEASE_MS })
          return false
        }
        return event.key === 'Tab'
      },
    },
  })
}

function isActive(mode: TabFocusMode, now: number) {
  return mode === 'on' || (typeof mode === 'object' && now <= mode.until)
}

function createAnnouncer(document: Document) {
  const region = document.createElement('div')
  region.setAttribute('aria-live', 'polite')
  Object.assign(region.style, {
    position: 'absolute',
    width: '1px',
    height: '1px',
    overflow: 'hidden',
    clipPath: 'inset(50%)',
    whiteSpace: 'nowrap',
  })
  document.body.append(region)
  return region
}
